import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import appCssInline from "virtual:app-css-inline";
import { assetRetryScript } from "@/lib/asset-retry-script";
import { HOSPITAL_SCHEMA, ORGANIZATION_SCHEMA, ORG_REF, PHYSICIAN_SAME_AS } from "@/lib/medical-schema";
import favicon32 from "../assets/favicons/favicon-32x32.png.asset.json";
import favicon192 from "../assets/favicons/favicon-192.png.asset.json";
import appleTouchIcon from "../assets/favicons/apple-touch-icon.png.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { TopBar } from "@/components/site/TopBar";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { EsFooter } from "@/components/site/EsFooter";
import { isSpanishPath } from "@/lib/language-switch";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GlobalErrorListeners } from "@/components/GlobalErrorListeners";
import { RumBeacon } from "@/components/site/RumBeacon";
import { measureConversion, measurePageVisit } from "@/lib/cta-measurement";
import { buildBreadcrumbSchema } from "@/lib/breadcrumbs";
import { NotFound } from "@/components/site/NotFound";
import { SkipToContent } from "@/components/site/SkipToContent";
import { clearLanguagePreference } from "@/lib/language-preference";
import { BreadcrumbNav } from "@/components/site/BreadcrumbNav";

// Non-empty only in the production server build. When set, the compiled
// stylesheet is embedded in the SSR HTML and the render-blocking stylesheet
// request is dropped, so text and layout paint on the first response.
// On the client the virtual module is empty, so adopt the CSS already present
// in the server-rendered <style> tag. Without this, React would re-render the
// tag with an empty body on the first client navigation and strip all styling.
const INLINE_CSS =
  appCssInline ||
  (typeof document !== "undefined"
    ? (document.querySelector('style[data-app-css="inline"]')?.innerHTML ?? "")
    : "");

function NotFoundComponent() {
  return <NotFound />;
}


function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: (ctx) => {
    const matches = (ctx as { matches?: Array<{ pathname?: string }> }).matches ?? [];
    const pathname = matches[matches.length - 1]?.pathname ?? "/";
    const breadcrumb = buildBreadcrumbSchema(pathname);
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "author", content: "Virginia Bariatric Surgery" },
        { name: "robots", content: "max-snippet:-1, max-image-preview:large" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        // fb:app_id intentionally omitted: with an app ID present, Facebook
        // treats the link as app-linked content and can open it in Messenger
        // instead of the article. The debugger's "missing fb:app_id" notice is
        // informational only — link previews render fine without it.
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        // og:image / twitter:image are intentionally NOT set here: root head
        // entries concatenate into every match and would override each leaf
        // route's own share preview.
      ],
      links: [
        // Dev keeps the normal stylesheet link (HMR owns it). Production
        // embeds the same CSS inline in the SSR HTML instead — see INLINE_CSS.
        ...(import.meta.env.DEV
          ? [
              {
                rel: "stylesheet",
                href: appCss,
              },
            ]
          : []),
        { rel: "preconnect", href: "https://www.googletagmanager.com", crossOrigin: "anonymous" },
        { rel: "preconnect", href: "https://www.google-analytics.com", crossOrigin: "anonymous" },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
        { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
        { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
        { rel: "icon", type: "image/png", sizes: "32x32", href: favicon32.url },
        { rel: "icon", type: "image/png", sizes: "192x192", href: favicon192.url },
        { rel: "apple-touch-icon", sizes: "180x180", href: appleTouchIcon.url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              // Canonical business (NAP) node — single source of truth in
              // src/lib/medical-schema.ts so home/About/root never diverge.
              ORGANIZATION_SCHEMA,
              HOSPITAL_SCHEMA,
              {
                "@type": "Person",
                "@id": "https://virginiabariatric.com/#physician-fitzer",
                name: "Matthew Fitzer, MD",
                givenName: "Matthew",
                familyName: "Fitzer",
                honorificSuffix: "MD",
                jobTitle: "Bariatric Surgeon",
                description:
                  "Board-certified, fellowship-trained bariatric surgeon serving the DC metro area since 2005, with over 6,000 laparoscopic weight-loss operations performed.",
                url: "https://virginiabariatric.com/about",
                mainEntityOfPage: "https://virginiabariatric.com/about",
                sameAs: PHYSICIAN_SAME_AS,
                image:
                  "https://virginiabariatric.com/__l5e/assets-v1/3d136713-9dce-42a2-87c9-b7016519b648/og-dr-fitzer.jpg",

                telephone: "+1-703-709-9771",
                knowsAbout: [
                  "Sleeve gastrectomy",
                  "Roux-en-Y gastric bypass",
                  "Endoscopic sleeve gastroplasty",
                  "Intragastric balloon (Orbera)",
                  "GLP-1 weight-loss medications",
                  "Obesity medicine",
                ],
                hasCredential: [
                  {
                    "@type": "EducationalOccupationalCredential",
                    credentialCategory: "Board Certification",
                    recognizedBy: {
                      "@type": "Organization",
                      name: "American Board of Surgery",
                    },
                  },
                  {
                    "@type": "EducationalOccupationalCredential",
                    credentialCategory: "Fellowship",
                    educationalLevel: "Minimally-Invasive Bariatric Surgery Fellowship",
                    recognizedBy: {
                      "@type": "CollegeOrUniversity",
                      name: "University of Missouri",
                    },
                  },
                ],
                alumniOf: [
                  { "@type": "CollegeOrUniversity", name: "Christopher Newport University" },
                  { "@type": "CollegeOrUniversity", name: "Eastern Virginia Medical School" },
                  {
                    "@type": "MedicalOrganization",
                    name: "Memorial Health University Medical Center",
                  },
                  { "@type": "CollegeOrUniversity", name: "University of Missouri" },
                ],
                worksFor: ORG_REF,
                affiliation: ORG_REF,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "171 Elden Street, Suite 3B",
                  addressLocality: "Herndon",
                  addressRegion: "VA",
                  postalCode: "20170",
                  addressCountry: "US",
                },
              },
              {
                "@type": "WebSite",
                "@id": "https://virginiabariatric.com/#website",
                url: "https://virginiabariatric.com/",
                name: "Virginia Bariatric Surgery",
                alternateName: ["Virginia Bariatric", "Virginia Bariatric Center", "Virginia Bariatric Surgery Center"],
                description:
                  "Northern Virginia's experienced bariatric surgery practice with Dr. Matthew Fitzer.",
                inLanguage: "en-US",
                publisher: ORG_REF,
                potentialAction: {
                  "@type": "SearchAction",
                  target: {
                    "@type": "EntryPoint",
                    urlTemplate: "https://virginiabariatric.com/search?q={search_term_string}",
                  },
                  "query-input": "required name=search_term_string",
                },
              },
            ],
          }),
        },
        ...(breadcrumb
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(breadcrumb),
              },
            ]
          : []),
      ],

    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const themeInitScript = `(function(){try{var t=localStorage.getItem('theme');if(!t){t=window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}if(t==='dark'){document.documentElement.classList.add('dark');document.documentElement.style.colorScheme='dark';}else{document.documentElement.style.colorScheme='light';}}catch(e){}})();`;

function RootShell({ children }: { children: ReactNode }) {
  // Spanish routes live under /es — declare the document language so crawlers
  // and screen readers treat the page as Spanish, matching its hreflang="es".
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => { measurePageVisit(pathname); }, [pathname]);
  const lang = pathname === "/es" || pathname.startsWith("/es/") ? "es" : "en";

  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        {/* Route meta/links first: the preload scanner sees the LCP image
            preload before it has to parse the large inline stylesheet. */}
        <HeadContent />
        {/* Production: the whole compiled stylesheet, embedded here so the
            browser can paint without waiting on a separate CSS request. Empty
            in dev and in the client bundle, where the <link> is used instead. */}
        <style
          data-app-css="inline"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: INLINE_CSS }}
        />

        <script dangerouslySetInnerHTML={{ __html: assetRetryScript }} />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Deletes any leftover vb-lang/vb_lang preference from the retired
    // auto-redirect feature. Every URL always shows its own page now.
    clearLanguagePreference();
    const onPhoneTap = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('a[href^="tel:"]')) {
        measureConversion("phone_tap");
      }
    };
    document.addEventListener("click", onPhoneTap, true);
    const w = window as typeof window & {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
      __gaLoaded?: boolean;
    };
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
      w.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer!.push(arguments);
      };
      w.gtag("js", new Date());
      w.gtag("config", "G-J0F0GW2GMW", { send_page_view: false });
    }

    // Defer gtag.js until the FIRST user interaction so it never runs during
    // the initial load window (FCP→TTI), eliminating its ~250 ms of Total
    // Blocking Time. Page-view events still queue in dataLayer and flush
    // once the script loads. SEO is unaffected: Google does not rank on GA
    // data, and Googlebot renders the page without needing analytics.
    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "wheel",
    ];
    const loadGa = () => {
      if (w.__gaLoaded) return;
      w.__gaLoaded = true;
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, loadGa, { capture: true }),
      );
      window.clearTimeout(fallbackTimer);
      const s = document.createElement("script");
      s.src = "https://www.googletagmanager.com/gtag/js?id=G-J0F0GW2GMW";
      s.async = true;
      document.head.appendChild(s);
    };
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, loadGa, { capture: true, passive: true }),
    );
    // Safety net: if the visitor never interacts, load after 5 s so bounce
    // page-views still get tracked without blocking the initial load window.
    const fallbackTimer = window.setTimeout(loadGa, 5000);

    // Initial page view. router.subscribe("onResolved") only fires on
    // subsequent client-side navigations, so without this the very common
    // "land on one page and leave" session reported nothing at all.
    w.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
      page_location: window.location.href,
    });

    const unsubscribe = router.subscribe("onResolved", (event) => {
      w.gtag?.("event", "page_view", {
        page_path: event.toLocation.pathname,
        page_title: document.title,
        page_location: window.location.href,
      });
    });

    return () => {
      document.removeEventListener("click", onPhoneTap, true);
      unsubscribe();
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, loadGa, { capture: true }),
      );
      window.clearTimeout(fallbackTimer);
    };
  }, [router]);



  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <GlobalErrorListeners />
        <RumBeacon />
        <SkipToContent />
        <TopBar />
        <Header />
        <BreadcrumbNav pathname={pathname} />
        <main
          id="main-content"
          tabIndex={-1}
          aria-label="Main content"
          className="flex-1 scroll-mt-24 outline-none"
        >
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <ErrorBoundary boundaryName="root_main_outlet">
            <Outlet />
          </ErrorBoundary>
        </main>
        {/* Spanish pages get the Spanish footer, which links only to pages we
            have in Spanish; every other page keeps the English footer. */}
        {isSpanishPath(pathname) ? <EsFooter /> : <Footer />}
      </div>
    </QueryClientProvider>
  );
}
